import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Network } from 'vis-network/standalone/esm/vis-network';
import 'font-awesome/css/font-awesome.min.css';

import { computeNetworkOptions } from './visjs_network_options';
import { nodeToVisId } from './visjs_object_conversion';
import { VisjsDatasetsHandler } from './data_handler';


const Graph = forwardRef((props, ref) => {
  const dsHandler = useRef(null)
  const network = useRef(null)
  const networkContainerRef = useRef(null)
  const paramsRefForCallbacks = useRef({})

  // workaround for `select` network event not firing on node drag (although it gets selected)
  const crtSelection = useRef({})

  function onVisClick(event) {
    //console.log("onVisClick: ", event)
  }

  function onVisDoubleClick(event) {
    // console.log("onVisDoubleClick: ", event)
    if (!dsHandler.current) return;
    if (paramsRefForCallbacks.current.onDoubleClickNode && event.nodes?.length === 1) {
      let userNode = dsHandler.current.getNode(event.nodes[0]);
      paramsRefForCallbacks.current.onDoubleClickNode(userNode);
    }
  }

  function onVisContext(event) {
    if (!paramsRefForCallbacks.current.onContext) return;
    event.event.preventDefault();
    let nodeId = network.current.getNodeAt(event.pointer.DOM);
    let node = null;
    if (nodeId) {
      node = dsHandler.current.crtNodes[nodeId] || null;
    }
    let cbEvent = {
      dom: event.pointer.DOM,
      canvas: event.pointer.canvas,
      node
    };
    paramsRefForCallbacks.current.onContext(cbEvent);
  }

  function selectionMaybeChanged(nodeIds, edgeIds) {
    let selectionStr = nodeIds.join(':') + '::' + edgeIds.join(':');
    if (crtSelection.current === selectionStr) return;
    crtSelection.current = selectionStr;

    if (!dsHandler.current) return;
    if (paramsRefForCallbacks.current.onSelectionChanged) {
      let nodes = [];
      let edges = [];
      for (let nodeId of nodeIds) {
        let userNode = dsHandler.current.getNode(nodeId);
        if (userNode) nodes.push(userNode);
      }
      for (let edgeId of edgeIds) {
        let userEdge = dsHandler.current.getEdge(edgeId);
        if (userEdge) edges.push(userEdge);
      }
      paramsRefForCallbacks.current.onSelectionChanged(nodes, edges);
    }
  }

  function onVisDragEnd(event) {
    const {nodes, edges} = network.current.getSelection();
    //console.log("onVisDragEnd: ", event, "Selection: ", nodes, edges);
    selectionMaybeChanged(nodes, edges);
  }

  function onVisSelect(event) {
    // selection changed (but it's buggy: not called if node was selected with a dragStart event)
    //console.log("onVisSelect: ", event)
    selectionMaybeChanged(event.nodes, event.edges);
  }

  function onVisBeforeDrawing(ctx) {
    if (!paramsRefForCallbacks.current.nodeMarkers) return;

    for (let marker of paramsRefForCallbacks.current.nodeMarkers) {
      if (!marker.inBackground || !marker.nodes || !marker.drawFunction) continue;
      let nodeIds = marker.nodes.map(nodeToVisId);
      const nodePos = network.current.getPositions(nodeIds);
      nodeIds = nodeIds.filter(nodeId => (nodeId in nodePos));
      nodeIds.forEach(id => marker.drawFunction(ctx, nodePos[id].x, nodePos[id].y));
    }
  }

  function onVisAfterDrawing(ctx) {
    if (!paramsRefForCallbacks.current.nodeMarkers) return;

    for (let marker of paramsRefForCallbacks.current.nodeMarkers) {
      if (marker.inBackground || !marker.nodes || !marker.drawFunction) continue;
      let nodeIds = marker.nodes.map(nodeToVisId);
      const nodePos = network.current.getPositions(nodeIds);
      nodeIds = nodeIds.filter(nodeId => (nodeId in nodePos));
      nodeIds.forEach(id => marker.drawFunction(ctx, nodePos[id].x, nodePos[id].y));
    }
  }

  function onVisStabilized(event) {
    // console.log("onVisStabilized: ", event)
  }

  useEffect(() => {
    if (network.current !== null) return;
    // create visjs network
    const options = props.overrideDefaultOptions ? {...(props.visjsOptions || {})} : computeNetworkOptions(props.visjsOptions);
    dsHandler.current = new VisjsDatasetsHandler(props.enableFilter);
    network.current = new Network(networkContainerRef.current, dsHandler.current.networkData, options)
    dsHandler.current.network = network.current;

    // https://visjs.github.io/vis-network/docs/network/#Events
    network.current.on('click', onVisClick)
    network.current.on('doubleClick', onVisDoubleClick)
    network.current.on('oncontext', onVisContext)
    network.current.on('select', onVisSelect)
    network.current.on('dragEnd', onVisDragEnd)

    network.current.on('beforeDrawing', onVisBeforeDrawing)
    network.current.on('afterDrawing', onVisAfterDrawing)
    network.current.on('stabilized', onVisStabilized)

    // for browser debugging purposes
    window.network = network.current;
    window.ds = dsHandler.current;
    // console.log("Created visjs network...")

    // for hacks only, exposing visjs network and dataset objects to parent components. This should not be needed
    props.onVisJsNetworkCreated && props.onVisJsNetworkCreated(network.current, dsHandler.current.nodesDS, dsHandler.current.edgesDS);

    // cleanup when unmounting
    return () => {
      if (network.current) {
        // console.log("destroying...");
        network.current.destroy();
        network.current = null;
        dsHandler.current = null;
      }
    }
  }, [])

  useEffect(() => {
    paramsRefForCallbacks.current = {
      onSelectionChanged: props.onSelectionChanged,
      onDoubleClickNode: props.onDoubleClickNode,
      onContext: props.onContext,
      nodeMarkers: props.nodeMarkers
    }
  }, [props.onSelectionChanged, props.onDoubleClickNode, props.onContext, props.nodeMarkers]);

  useEffect(() => {
    if (!dsHandler.current) return;
    dsHandler.current.setNodesAndEdges(
      props.data?.nodes, 
      props.data?.edges, 
      {setNodesPosition: Boolean(props.data?.setNodesPosition)}
    );
    if (props.data?.fit) {
      setTimeout(() => network.current.fit(), 0);
    }
    // check if selection was impacted (need to fire new selection event with updated data)
    const {nodes, edges} = network.current.getSelection();
    if (nodes.some(id => (id in dsHandler.current.changedNodeIds)) || edges.some(id => (id in dsHandler.current.changedEdgeIds))) {
      crtSelection.current = '';
      selectionMaybeChanged(nodes, edges);
    }
  }, [dsHandler, props.data])

  useEffect(() => {
    if (!network.current) return;
    const options = props.overrideDefaultOptions ? {...(props.visjsOptions || {})} : computeNetworkOptions(props.visjsOptions);
    network.current.setOptions(options);
    // console.log("Set options to ", options);
  }, [props.visjsOptions]);
  
  useEffect(() => {
    if (!props.filter) return;
    dsHandler.current.setFilter(props.filter.node, props.filter.edge);
  }, [props.filter]);

  useEffect(() => {
    network.current?.redraw();
  }, [props.nodeMarkers]);

  useImperativeHandle(ref, () => ({
    // Workaround visjs issue:
    // after deleting a selected node, network keeps returning the old id for getSelectedNodes() even if not in dataset.
    // also, selection update is not happening
    // So we manually call selectNodes after removing selected nodes, with the remaining selected nodes
    selectNodes(nodeList) {
      const nodeIds = nodeList.map(node => nodeToVisId(node));
      network.current.selectNodes(nodeIds);
      const {nodes, edges} = network.current.getSelection();
      selectionMaybeChanged(nodes, edges);
    },

    getNodesWithPosition() {
      const positions = network.current.getPositions();
      const nodesWithPositions = dsHandler.current.getNodesWithPos(positions);
      return nodesWithPositions;
    },

    getNodePositions(nodes) {
      const nodeIds = nodes.map(node => nodeToVisId(node));
      const positions = network.current.getPositions(nodeIds);
      const ret = [];
      for (let node of nodes) {
        const nodeId = nodeToVisId(node);
        ret.push({type: node.type, value: node.value, position: (positions[nodeId] || {x: 0, y: 0})});
      }
      return ret;
    },

    getCenterPosition(nodes) {
      if (!nodes || nodes.length === 0) return {x: 0, y: 0};
      const nodeIds = nodes.map(node => nodeToVisId(node));
      const positions = network.current.getPositions(nodeIds);
      let sumX = 0, sumY = 0;
      for (let nodeId in positions) {
        const pos = positions[nodeId];
        sumX += pos.x;
        sumY += pos.y;
      }
      return {x: Math.floor(sumX / nodes.length), y: Math.floor(sumY / nodes.length)};
    },

    moveTo(position, options) {
      network.current.moveTo({position: position, animation: {duration: 200, easingFunction: 'easeInOutCubic'}, ...(options || {})});
    }

    // addEdge() {
    //   console.log("Adding edge...");
    //   network.current?.addEdgeMode();
    // }
  }));

  return (
    <div
      className="athenalib-graph"
      style={props.size ? { width: props.size.width || '100%', height: props.size.height || '100%' } : {}}
      onKeyDown={props.onKeyDown}
      onKeyUp={props.onKeyUp}
      ref={networkContainerRef}
    >
    </div>
  )
});

export default Graph
